import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {Subscription} from 'rxjs';
import {BdManualContractDecisionService} from '../../../service/api/bd-manual-contract-decision.service';
// TM-1000001933: scoped Stornogrund (cancellation reason) dropdown (area=INKASSO_HANDOVER).
import {DunningService} from '../../../service/api/dunning.service';
import {BdDunningStornoReasonDto} from '../../../model/dto/api/bd-dunning-storno-reason.dto';
import {BdDunningStornoReasonScopeEnum} from '../../../enum/bd-dunning-storno-reason-scope.enum';

/**
 * Dropdown option for the contract selection field.
 */
interface ContractOption {
    label: string;
    value: number;
}

/**
 * AI:
 * Status: 'wave-3-implementation'
 * Type: Component
 * SubType: Exit-UI / Handover-Configuration
 * Reason: SB-UI zur Konfiguration einer Inkasso-Übergabe BEVOR sie zur Freigabe
 *         eingereicht wird. Sammelt contractId + reason und ruft
 *         {@code POST /manual-contract-decision/handover} mit action='HANDOVER' auf.
 *
 *         TM-1000002054: the hand-over is approved by a user with the role admin,
 *         bd_admin or bd_approver, also by the submitter himself.
 *
 *         Mapped to the (planned) {@code BdDunningExitUiComponent.HANDOVER_CONFIGURATION}
 *         enum value. Wenn dieser Wert im Enum fehlt (Wave 3 vor Wave 4), bleibt die
 *         Komponente trotzdem registriert und wird vom Wave-4-Backend-Push verlinkt.
 *
 * @ticket TM-1000001786
 * @ticket TM-1000002054
 */
@Component({
    selector: 'bd-handover-configuration',
    templateUrl: './bd-handover-configuration.component.html',
    styleUrls: ['./bd-handover-configuration.component.scss'],
    standalone: false
})
export class BdHandoverConfigurationComponent implements OnInit, OnDestroy {

    // ----------------------------------------------------------------- //
    // Inputs
    // ----------------------------------------------------------------- //

    @Input() dunning: Record<string, any> | null = null;
    @Input() contracts: any[] | null = null;
    @Input() uiComponentConfig: Record<string, any> | null = null;

    // U17: Vom Drawer (ngComponentOutletInputs) durchgereichte Felder. Nicht
    // direkt verwendet, aber als @Input() deklariert, damit die Drawer-Bindung
    // kein NG0303 auslöst.
    @Input() dunningId: number | null = null;
    @Input() handOvers: unknown[] | null = null;
    @Input() activePause: unknown | null = null;
    @Input() breachedPlan: unknown | null = null;
    @Input() currentUserId: number | null = null;

    // ----------------------------------------------------------------- //
    // Outputs
    // ----------------------------------------------------------------- //

    @Output() actionSubmitted = new EventEmitter<{contractId: number}>();

    // ----------------------------------------------------------------- //
    // State
    // ----------------------------------------------------------------- //

    initialized: boolean = false;
    submitting: boolean = false;
    success: boolean = false;
    errorMessage: string | null = null;

    contractOptions: ContractOption[] = [];
    selectedContractId: number | null = null;
    reason: string = '';

    /**
     * TM-1000001933: Scope-gefilterte Stornogrund-Optionen (area=INKASSO_HANDOVER)
     * für das OPTIONALE „Stornogrund"-Dropdown. Leere Liste blendet es aus.
     */
    stornoReasons: BdDunningStornoReasonDto[] = [];

    /** TM-1000001933: Gewählter Stornogrund (null = „Standardgrund verwenden"). */
    selectedReasonTypeId: number | null = null;

    /** TM-1000001933: Subscription des lazy Stornogrund-Ladens. */
    private stornoReasonsSubscription: Subscription | null = null;

    constructor(
        private readonly manualContractDecisionService: BdManualContractDecisionService,
        // TM-1000001933: liefert die scope-gefilterte Stornogrund-Liste.
        private readonly dunningService: DunningService
    ) {}

    // ----------------------------------------------------------------- //
    // Lifecycle
    // ----------------------------------------------------------------- //

    ngOnInit(): void {
        this.contractOptions = this.buildContractOptions(this.contracts);
        if (this.contractOptions.length === 1) {
            this.selectedContractId = this.contractOptions[0].value;
        }
        // TM-1000001933: scope-gefilterte Stornogrund-Optionen lazy laden.
        this.loadStornoReasons();
        this.initialized = true;
    }

    ngOnDestroy(): void {
        this.stornoReasonsSubscription?.unsubscribe();
    }

    /** TM-1000001933: true, wenn scope-gefilterte Stornogrund-Optionen vorliegen. */
    get hasStornoReasons(): boolean {
        return this.stornoReasons.length > 0;
    }

    /**
     * TM-1000001933: Resolved die Mahnungs-Id (aus dem {@code dunning}-Objekt oder
     * dem durchgereichten {@code dunningId}-Input).
     */
    private resolveDunningId(): number | null {
        const fromObject = this.dunning?.['id'] ?? this.dunning?.['dunningId'];
        if (typeof fromObject === 'number') {
            return fromObject;
        }
        return typeof this.dunningId === 'number' ? this.dunningId : null;
    }

    /**
     * TM-1000001933: Lädt die scope-gefilterten Stornogrund-Optionen
     * (area=INKASSO_HANDOVER). Fehler werden still verschluckt (das Dropdown bleibt
     * dann ausgeblendet — kein harter Fehler für den Sachbearbeiter).
     */
    private loadStornoReasons(): void {
        const dunningId = this.resolveDunningId();
        if (dunningId == null) {
            return;
        }
        this.stornoReasonsSubscription?.unsubscribe();
        this.stornoReasonsSubscription = this.dunningService
            .getStornoReasons(dunningId, BdDunningStornoReasonScopeEnum.INKASSO_HANDOVER)
            .subscribe({
                next: (reasons) => {
                    this.stornoReasons = reasons ?? [];
                },
                error: () => {
                    this.stornoReasons = [];
                }
            });
    }

    // ----------------------------------------------------------------- //
    // Actions
    // ----------------------------------------------------------------- //

    submit(): void {
        const dunningId = this.dunning?.['id'] ?? this.dunning?.['dunningId'];
        if (dunningId == null) {
            this.errorMessage = 'Keine Mahnung referenziert.';
            return;
        }
        if (this.selectedContractId == null) {
            this.errorMessage = 'Bitte einen Vertrag auswählen.';
            return;
        }
        if (!this.reason || this.reason.trim().length === 0) {
            this.errorMessage = 'Bitte eine Begründung angeben.';
            return;
        }
        this.errorMessage = null;
        this.submitting = true;

        this.manualContractDecisionService.submitHandOver(
            dunningId,
            this.selectedContractId,
            'HANDOVER',
            this.reason.trim(),
            // submittedByUserId derives server-side from the security context.
            undefined,
            // TM-1000001933: chosen Stornogrund (null = area default at execution).
            this.selectedReasonTypeId
        ).subscribe({
            next: () => {
                this.submitting = false;
                this.success = true;
                this.actionSubmitted.emit({contractId: this.selectedContractId!});
            },
            error: (err) => {
                this.submitting = false;
                this.errorMessage = this.extractError(err);
            }
        });
    }

    // ----------------------------------------------------------------- //
    // Internal helpers
    // ----------------------------------------------------------------- //

    private buildContractOptions(contracts: any[] | null): ContractOption[] {
        if (!contracts) {
            return [];
        }
        return contracts.map(c => ({
            label: c?.identifier
                ? `#${c?.id ?? '?'} — ${c.identifier}`
                : `Vertrag #${c?.id ?? '?'}`,
            value: c?.id ?? c?.contractId ?? 0
        })).filter(o => o.value > 0);
    }

    private extractError(err: unknown): string {
        if (err && typeof err === 'object') {
            const anyErr = err as any;
            return anyErr.error?.message ?? anyErr.message ?? 'Unbekannter Fehler.';
        }
        return 'Unbekannter Fehler.';
    }
}
